import React, { SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlock } from 'i18n';
import { trackEvent } from 'helpers/trackerHelpers';
import { camelize } from 'helpers/syntaxHelper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';
import { cleanChartData } from 'helpers/chartHelper';
import ModalHeader from './components/ModalHeader';
import ModalContent from './components/ModalContent';
import ModalTitle from './components/ModalTitle';
import {
    useDetailsDataQuery,
    useExportDetailsDataQuery,
} from './hooks/detailsData';
import RawDataErrorModal from './components/RawDataErrorModal';

type Props = {
    chartTitle: string;
    closeModal: () => void;
    columns: ResponseColumnT[];
    dimensions: QueryDimensionT[];
    extraFilters: QueryFilterT[];
    filters: QueryFilterT[];
    metrics: QueryMetricT[];
    point?: ResponseDataT;
    show: boolean;
};

const StyDataModal = styled(DialogContent, {
    name: 'StyDataModal',
    shouldForwardProp: (prop) => prop !== 'isLoading',
})<{ isLoading: boolean }>(({ isLoading }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '80vw',
    minHeight: '20vh',
    padding: '0 !important',
    ...(isLoading
        ? {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
          }
        : {}),
}));

/**
 * Display details data for the selected point.
 * The table shows the data without any aggregation, based on what the user just clicked.
 * As a general rule, what the user will see will match the values in the tooltip.
 * So if in the tooltip you have a serie, you will see the data for the entire serie.
 * @type {Object}
 */
const RawDataModal = ({
    chartTitle,
    closeModal,
    columns,
    dimensions,
    extraFilters,
    filters,
    metrics,
    point = undefined,
    show,
}: Props): JSX.Element => {
    const [selectedMetric, setSelectedMetric] = React.useState(metrics[0]);
    const getModel = (metric: QueryMetricT) => metric.field.split('.')[0];

    const getModelsFromMetrics = useCallback(() => {
        const allModels = metrics.map(getModel);
        return Array.from(new Set(allModels));
    }, [metrics]);

    const uniqueModels = getModelsFromMetrics();
    const { t } = useTranslation();

    const getDetailsDataPayload = {
        metric: selectedMetric,
        dimensions,
        filters,
        dashboardFilters: extraFilters,
        columns,
        point,
    };
    const {
        data: rawData,
        error,
        isLoading,
    } = useDetailsDataQuery(getDetailsDataPayload);

    const { mutate } = useExportDetailsDataQuery(
        getDetailsDataPayload,
        chartTitle || translateBlock(selectedMetric.field),
    );

    const exportToFile = () => {
        mutate();
    };

    const detailsColumns =
        !isLoading && rawData
            ? rawData.columns.map((column: ResponseColumnT) => ({
                  ...column,
                  key: camelize(column.key),
                  label: translateBlock(camelize(column.key)),
              }))
            : [];
    const detailsData =
        !isLoading && rawData
            ? cleanChartData(rawData.data, detailsColumns)
            : [];

    const handleMetricTabChange = useCallback(
        (event: SyntheticEvent, metricField: string) => {
            const newSelectedMetric = metrics.find(
                (m) => m.field === metricField,
            );
            if (newSelectedMetric) setSelectedMetric(newSelectedMetric);
        },
        [metrics],
    );
    // tracking on amplitude when user opens the raw data modal
    React.useEffect(() => {
        if (show) trackEvent('decision/indicator/click-accès-données-brutes');
    }, [show]);

    if (error) {
        return (
            <RawDataErrorModal
                show={show}
                errorCode={error.status}
                errorMessage={error.message}
                closeModal={closeModal}
            />
        );
    }
    return (
        <Dialog open={show} onClose={closeModal} maxWidth={false}>
            <StyDataModal isLoading={isLoading}>
                <ModalTitle title={chartTitle} />
                <ModalHeader
                    selectedMetric={selectedMetric}
                    onChange={handleMetricTabChange}
                    uniqueModels={uniqueModels}
                    metrics={metrics}
                />
                <ModalContent
                    metrics={metrics}
                    selectedMetric={selectedMetric}
                    chartTitle={chartTitle}
                    detailsData={detailsData}
                    detailsColumns={detailsColumns}
                    exportToFile={exportToFile}
                    isLoading={isLoading}
                />
            </StyDataModal>
            <DialogActions>
                <Button
                    onClick={closeModal}
                    className="e2e-close-raw-data-button"
                >
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
export default RawDataModal;
